import React, { useEffect, useReducer } from 'react';
import { useMutation, useApolloClient } from '@apollo/client';
import { ToastProvider } from '@blaze-react/toaster';
import { EcommerceContextProvider } from './EcommerceContext';
import {
  LOCAL_CART_KEY,
  LOCAL_CURRENCY_KEY,
  LOCAL_CART_UPDATED_KEY,
  CONTEXT_KEYS,
  DEFAULT_CURRENCY,
  EVENT_NAMES
} from '../constants';
import {
  ADD_ITEMS_TO_CART_MUTATION,
  CART_CHECKOUT_MUTATION,
  CREATE_CART_MUTATION,
  CREATE_USER_ADDRESS,
  DELETE_ITEMS_FROM_CART,
  DELETE_USER_ADDRESS,
  UPDATE_CART_ITEMS_MUTATION,
  UPDATE_CART_MUTATION,
  UPDATE_USER_ADDRESS,
  ADD_DISCOUNT_TO_CART_MUTATION,
  REMOVE_DISCOUNT_FROM_CART_MUTATION,
  CREATE_RECREATE_MUTATION
} from '../services';
import { useGetCart, useGetUserAddresses } from '../hooks';
import formatCartState from '../utils/format-cart-state';
import CartMessagesComponent from '../components/CartMessagesComponent';
import ecommerceReducer from './ecommerceReducer';

const defaultCurrency = process.env.BLAZE_ECOMMERCE_DEFAULT_CURRENCY || DEFAULT_CURRENCY;

const EcommerceContextWrapper = ({
  withCredentials,
  children,
  disableBillingAddressUpdates,
  logoutGuestUsersOnComplete = true,
  enableEmitCartEvents = true
}) => {
  const { cartIdKey, currencyKey, cartDataKey } = CONTEXT_KEYS;
  const [contextState, dispatch] = useReducer(
    ecommerceReducer,
    formatCartState({
      currency: defaultCurrency,
      cartId: null,
      isCartLoading: false,
      cartData: null,
      initDone: false,
      selectedPaymentType: null,
      fetchPaymentDetails: false,
      selectedPaymentDetails: null,
      disableBillingAddressUpdates
    })
  );
  const client = useApolloClient();

  const { cartId, currency, cartData, initDone, selectedPaymentType } = contextState;

  const apolloContext = { credentials: withCredentials ? 'include' : null };
  const commonApolloOptions = { context: apolloContext };

  const persistedCartId = (id, isUpdate) => {
    localStorage.setItem(LOCAL_CART_KEY, id || ''); // don't set to undefined or null
    if (isUpdate) localStorage.setItem(LOCAL_CART_UPDATED_KEY, Date.now()); // let other tabs know about the update
  };

  const setCartState = newState => {
    dispatch(newState);
  };

  const updateCartId = newCartId => {
    persistedCartId(newCartId);
    setCartState({
      cartId: newCartId
    });
  };

  const handleCartData = ({
    result,
    isUpdate = true,
    updateContext = true,
    callback = () => {},
    event
  }) => {
    const { cartData: resultData } = result || {};
    const newCartData = resultData && resultData.result ? resultData.result : resultData; // handle differences between mutation and query
    const { id: newCartId, currency: cartCurrency } = newCartData || {};

    emitCartEvent(event, { cartData: newCartData, oldCartData: cartData });

    persistedCartId(newCartId, isUpdate);
    if (!updateContext) return callback(result);

    // todo: remove when apollo cache is used
    setCartState({
      currency: cartCurrency,
      cartId: newCartId,
      cartData: newCartData
    });
    callback(result);
  };

  const setSelectedPaymentDetails = (details, fetchPaymentDetails = false) => {
    setCartState({
      selectedPaymentDetails: details,
      fetchPaymentDetails
    });
  };

  const clearCart = async () => {
    localStorage.removeItem(LOCAL_CART_KEY);
    if (logoutGuestUsersOnComplete) {
      const { default: logoutGuestUser } =
        (await import(/* webpackChunkName: 'blazeEcommerceGuestLogout' */ '../utils/logout-guest-user')) ||
        {};
      if (logoutGuestUser) await logoutGuestUser(client);
    }
    setCartState({
      [cartDataKey]: null,
      [cartIdKey]: null
    });
  };

  const emitCartEvent = async (event, extraData) => {
    if (!event || !enableEmitCartEvents) return;

    const { default: emitCartEventFn } =
      (await import(/* webpackChunkName: 'blazeEcommerceEmitCartEvent' */ '../utils/emit-cart-event')) ||
      {};
    if (emitCartEventFn) {
      const extraDataToUse = extraData || { cartData, currency };
      await emitCartEventFn(event, extraDataToUse, client);
    }
  };

  const {
    loading: getCartLoading,
    error: getCartError,
    refetch: getCart,
    cartData: rawCartData
  } = useGetCart({
    id: cartId,
    currency,
    cartData,
    onCompleted: result => handleCartData({ result, isUpdate: false }),
    ...commonApolloOptions
  });

  const {
    userAddresses,
    loading: getUserAddressesLoading,
    refetch: refetchGetUserAddresses
  } = useGetUserAddresses(commonApolloOptions);

  const [createCart, { loading: createCartLoading }] = useMutation(
    CREATE_CART_MUTATION,
    commonApolloOptions
  );

  const [updateCart, { loading: updateCartLoading }] = useMutation(
    UPDATE_CART_MUTATION,
    commonApolloOptions
  );

  const [addDiscountToCart, { loading: addDiscountLoading }] = useMutation(
    ADD_DISCOUNT_TO_CART_MUTATION
  );

  const [removeDiscountFromCart, { loading: removeDiscountLoading }] = useMutation(
    REMOVE_DISCOUNT_FROM_CART_MUTATION,
    commonApolloOptions
  );

  const [addItemsToCart, { loading: addItemsToCartLoading }] = useMutation(
    ADD_ITEMS_TO_CART_MUTATION,
    commonApolloOptions
  );

  const [updateCartItems, { loading: updateCartItemsLoading }] = useMutation(
    UPDATE_CART_ITEMS_MUTATION,
    commonApolloOptions
  );

  const [deleteItemsFromCart, { loading: deleteItemsFromCartLoading }] = useMutation(
    DELETE_ITEMS_FROM_CART,
    commonApolloOptions
  );

  const [createCartCheckout, { loading: createCartCheckoutLoading }] = useMutation(
    CART_CHECKOUT_MUTATION,
    commonApolloOptions
  );

  const [recreateCart, { loading: recreateCartLoading }] = useMutation(
    CREATE_RECREATE_MUTATION,
    commonApolloOptions
  );

  const [createUserAddress, { loading: createUserAddressLoading }] = useMutation(
    CREATE_USER_ADDRESS,
    commonApolloOptions
  );

  const [updateUserAddress, { loading: updateUserAddressLoading }] = useMutation(
    UPDATE_USER_ADDRESS,
    commonApolloOptions
  );

  const [deleteUserAddress, { loading: deleteUserAddressLoading }] = useMutation(
    DELETE_USER_ADDRESS,
    commonApolloOptions
  );

  useEffect(
    () => {
      if (!initDone) return;

      if (localStorage.getItem(LOCAL_CURRENCY_KEY) !== currency && currency) {
        localStorage.setItem(LOCAL_CURRENCY_KEY, currency);
      }
    },
    [currency, initDone]
  );

  useEffect(
    () => {
      // listen for id changes in another tab
      function checkCartId(event) {
        const { key } = event;
        const storageCartId = localStorage.getItem(LOCAL_CART_KEY);
        const storageCurrency = localStorage.getItem(LOCAL_CURRENCY_KEY) || currency;

        const hasChanges =
          storageCartId !== cartId ||
          storageCurrency !== currency ||
          key === LOCAL_CART_UPDATED_KEY;
        if (hasChanges) {
          getCart({ id: storageCartId, currency: storageCurrency });
        }
      }

      window.addEventListener('storage', checkCartId);

      return () => {
        window.removeEventListener('storage', checkCartId);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cartId, currency]
  );

  useEffect(
    () => {
      function initContextState() {
        const localCurrency = localStorage.getItem(LOCAL_CURRENCY_KEY) || defaultCurrency;
        const localCartId = localStorage.getItem(LOCAL_CART_KEY);

        setCartState({
          initDone: true,
          currency: localCurrency,
          cartId: localCartId
        });
        if (localCartId && localCurrency) getCart({ id: localCartId, currency: localCurrency });
      }
      initContextState();
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(
    () => {
      if (getCartError) {
        localStorage.removeItem(LOCAL_CART_KEY);
        setCartState({
          cartId: null
        });
      }
    },
    [contextState, getCartError]
  );

  const updateCurrency = newCurrency => {
    if (cartId) {
      return getCart({ id: cartId, currency: newCurrency });
    }

    stateHandler(currencyKey, newCurrency);
  };

  const stateHandler = (key, value) => {
    if (key === 'selectedPaymentType' && value === selectedPaymentType) return; // todo: potentially do this for all keys

    const newCurrency = key === currencyKey ? value : currency;

    setCartState({
      currency: newCurrency,
      [key]: value
    });
  };

  const handleCreateCart = ({ lineItems, onCompleted = () => {}, onError = () => {} }) => {
    createCart({
      variables: {
        currency,
        lineItems
      },
      onCompleted: result =>
        handleCartData({
          result,
          callback: onCompleted,
          event: { eventName: EVENT_NAMES.create_cart, input: lineItems }
        }),
      onError
    });
  };

  const handleUpdateCart = ({ input, onCompleted = () => {}, onError = () => {} }) => {
    updateCart({
      variables: {
        input: {
          ...input,
          id: cartId
        }
      },
      onCompleted: result =>
        handleCartData({
          result,
          callback: onCompleted,
          event: { eventName: EVENT_NAMES.update_cart, input }
        }),
      onError
    });
  };

  const handleAddItemsToCart = ({ lineItems, onCompleted = () => {}, onError = () => {} }) => {
    addItemsToCart({
      variables: {
        id: cartId,
        currency,
        lineItems
      },
      onCompleted: result =>
        handleCartData({
          result,
          callback: onCompleted,
          event: { eventName: EVENT_NAMES.add_to_cart, input: lineItems }
        }),
      onError
    });
  };

  const handleUpdateCartItems = ({ lineItems, onCompleted = () => {}, onError = () => {} }) => {
    updateCartItems({
      variables: {
        id: cartId,
        currency,
        lineItems
      },
      onCompleted: result =>
        handleCartData({
          result,
          callback: onCompleted,
          event: { eventName: EVENT_NAMES.update_cart, input: lineItems }
        }),
      onError
    });
  };

  const handleDeleteCartItems = ({ lineItems, onCompleted = () => {}, onError = () => {} }) => {
    deleteItemsFromCart({
      variables: {
        id: cartId,
        lineItems
      },
      onCompleted: result =>
        handleCartData({
          result,
          callback: onCompleted,
          event: { eventName: EVENT_NAMES.remove_from_cart, input: lineItems }
        }),
      onError
    });
  };

  const handleCartCheckout = ({ input, onCompleted = () => {}, onError = () => {} }) => {
    setSelectedPaymentDetails(null);
    createCartCheckout({
      variables: {
        input: {
          ...input,
          id: cartId,
          currency
        }
      },
      onCompleted,
      onError
    });
  };

  const handleRecreateCart = ({ input = {}, onCompleted = () => {}, onError = () => {} } = {}) => {
    recreateCart({
      variables: {
        id: cartId,
        currency,
        ...input
      },
      onCompleted: result => handleCartData({ result, callback: onCompleted }),
      onError
    });
  };

  const handleAddDiscountToCart = ({ input, onCompleted = () => {}, onError = () => {} }) => {
    addDiscountToCart({
      variables: {
        ...input,
        id: cartId
      },
      onCompleted: result =>
        handleCartData({
          result,
          callback: onCompleted,
          event: { eventName: EVENT_NAMES.select_promotion, input }
        }),
      onError
    });
  };

  const handleRemoveDiscountFromCart = ({ input, onCompleted = () => {}, onError = () => {} }) => {
    removeDiscountFromCart({
      variables: {
        ...input,
        id: cartId
      },
      onCompleted: result =>
        handleCartData({
          result,
          callback: onCompleted,
          event: { eventName: EVENT_NAMES.remove_promotion, input }
        }),
      onError
    });
  };

  const handleCreateUserAddress = ({ variables, onCompleted = () => {}, onError = () => {} }) => {
    createUserAddress({
      variables,
      onCompleted,
      onError
    });
  };

  const handleUpdateUserAddress = ({ variables, onCompleted = () => {}, onError = () => {} }) => {
    updateUserAddress({
      variables,
      onCompleted,
      onError
    });
  };

  const handleDeleteUserAddress = ({ variables, onCompleted = () => {}, onError = () => {} }) => {
    deleteUserAddress({
      variables,
      onCompleted,
      onError
    });
  };

  const isCartLoading =
    getCartLoading ||
    createCartLoading ||
    updateCartLoading ||
    addItemsToCartLoading ||
    updateCartItemsLoading ||
    deleteItemsFromCartLoading ||
    createCartCheckoutLoading ||
    removeDiscountLoading ||
    addDiscountLoading ||
    recreateCartLoading;

  const isAddressesLoading =
    getUserAddressesLoading ||
    createUserAddressLoading ||
    updateUserAddressLoading ||
    deleteUserAddressLoading;

  const isBasketEmpty = !cartData || !cartData.lineItems || !cartData.lineItems.length;

  return (
    <EcommerceContextProvider
      value={{
        ...contextState,
        clearCart,
        updateCartId,
        cartData,
        userAddresses,
        refetchGetUserAddresses,
        isCartLoading,
        isAddressesLoading,
        isBasketEmpty,
        cardDataLoading: rawCartData && !cartData,
        handleCreateCart,
        handleUpdateCart,
        handleAddItemsToCart,
        handleUpdateCartItems,
        handleDeleteCartItems,
        handleCartCheckout,
        handleCreateUserAddress,
        handleUpdateUserAddress,
        handleDeleteUserAddress,
        handleAddDiscountToCart,
        handleRemoveDiscountFromCart,
        handleRecreateCart,
        updateCurrency,
        setSelectedPaymentDetails,
        setContextState: stateHandler,
        emitCartEvent
      }}>
      <ToastProvider>
        {children}
        <CartMessagesComponent cartData={cartData} />
      </ToastProvider>
    </EcommerceContextProvider>
  );
};

export default EcommerceContextWrapper;
